import * as _ from "lodash";
import moment from "moment";
import * as React from "react";
import Select, { components } from "react-select";
import { OutotecIcons } from "../Components/General/Icons";
import * as Const from "../Constants";
import * as Models from "../Models";
import { comparisonOperators } from "./forms";

export const searchGroupTypeToTitle: (
    type: Models.SearchSettingGroupType
) => string = (type: Models.SearchSettingGroupType) => {
    switch (Number(type)) {
        case 1:
            return "Asset filters";
        case 2:
            return "Location filters";
        case 3:
            return "Customer filters";
        case 4:
            return "Product filters";
        case 5:
            return "Document filters";
        default:
            return "Group type not found";
    }
};

export const searchIndexToDisplayName: (index: string) => string = (
    index: string
) => {
    switch (index) {
        case Const.SEARCH_RESULT_BUILDS:
        case Const.SEARCH_FILTER_GROUP_BUILD:
            return "assets";
        default:
            return index;
    }
};

export const searchGroupTypeToColumnKey: (
    type: Models.SearchSettingGroupType
) => string = (type: Models.SearchSettingGroupType) => {
    switch (Number(type)) {
        case 1:
            return Const.SEARCH_FILTER_GROUP_BUILD.toLocaleLowerCase();
        case 2:
            return Const.SEARCH_FILTER_GROUP_LOCATION.toLocaleLowerCase();
        case 3:
            return Const.SEARCH_FILTER_GROUP_CUSTOMER.toLocaleLowerCase();
        case 4:
            return Const.SEARCH_FILTER_GROUP_PRODUCT.toLocaleLowerCase();
        case 5:
            return Const.SEARCH_FILTER_GROUP_DOCUMENT.toLocaleLowerCase();
        default:
            return "Group type not found";
    }
};

export const searchResultsToAttributeGroup: Function = (
    resultSet: any[],
    columns: Models.SearchResultColumn[],
    searchIndex: string
): Models.AttributeGroup => {
    let dict = {};
    let activeColumns: Models.SearchResultColumn[] = _.sortBy(
        _.filter(columns, (c) => c.active),
        ["order", "displayName"]
    );

    _.forEach(activeColumns, (column) => {
        if (column.classAttribute && !column.displayName.endsWith("UoM")) {
            const displayName: string = column.displayName + " UoM";
            if (
                activeColumns.filter((x) => x.displayName === displayName)
                    .length <= 0
            ) {
                const columnIndex: number = activeColumns.findIndex((item) => {
                    return item.searchField === column.searchField;
                });
                var uomCol: Models.SearchResultColumn = {
                    active: column.active,
                    classAttribute: column.classAttribute,
                    columnFieldInResultViews: column.columnFieldInResultViews,
                    displayName: displayName,
                    filterable: column.filterable,
                    isColumn: column.isColumn,
                    order: column.order,
                    filterGroupOrder: column.filterGroupOrder,
                    searchField: column.searchField + "Uom",
                    searchResultOrders: column.searchResultOrders,
                };
                dict[(columnIndex + 1).toString()] = uomCol;
            }
        }
    });

    for (var key in dict) {
        var value = dict[key];
        activeColumns.splice(Number(key), 0, value);
    }

    const attributeGroup: Models.AttributeGroup = {
        headers: activeColumns.map((c) => c.displayName),
        name: searchIndexToDisplayName(searchIndex),
        rows: [],
        filterableColumns: _.map(
            activeColumns.filter((c) => c.filterable),
            (col) => col.displayName
        ),
    };

    _.forEach(resultSet, (result: any, i: number) => {
        const row: Models.AttributeRow = {
            items: [],
        };

        _.forEach(
            activeColumns.map((c) => c),
            (activeColumn) => {
                const colName: string = activeColumn.searchField;
                const displayName = activeColumn?.displayName;
                const column: Models.AttributeCell = getRowColumnValue(
                    colName,
                    result,
                    searchIndex,
                    displayName
                );
                row.items.push(column);
            }
        );

        attributeGroup.rows.push(row);
    });

    return attributeGroup;
};

const addRowItem: (
    row: Models.AttributeRow,
    attributeGroup: Models.AttributeGroup,
    column: Models.AttributeCell,
    role: string
) => void = (row, attributeGroup, column, role) => {
    // Find the first role based column so that we can group columns based on role
    const firstRoleColIndex: number = attributeGroup.headers.findIndex(
        (c) => c.toLowerCase().indexOf(role) >= 0
    );

    if (firstRoleColIndex >= 0) {
        row.items.splice(firstRoleColIndex + 1, 0, column);
    } else {
        row.items.push(column);
    }
};

const getRowColumnValue: (
    colName: string,
    result: any,
    searchIndex: string,
    displayName: string
) => Models.AttributeCell = (colName, result, searchIndex, displayName) => {
    const columnName: string = _.lowerFirst(colName);
    let description: string = result[columnName];
    if (typeof result[columnName] === "boolean") {
        description = result[columnName].toString();
    }
    let url: Models.Link;
    let id: string;
    let toolTip: string;
    let additionalInfo: string;

    if (columnName.toLowerCase().indexOf("date") > -1) {
        const date: moment.Moment = moment(result[columnName]);
        if (date.isValid()) {
            description = date.format("YYYY-MM-DD");
        }
    }

    switch (columnName) {
        case "buildName":
        case "buildSerialNumber":
        case "buildDescription":
            url = {
                additionalInfo: "",
                id: result.internalId,
                type: Const.SITE_TREE,
                description,
                url: `/api/${Const.SITE_TREE}/${result.internalId}`,
                openToNewTab: true,
            };
            description = "";
            break;
        case "locationDescription":
        case "locationName":
        case "siteName":
            if (searchIndex !== Const.SEARCH_RESULT_LOCATIONS) {
                id = result.locationId;
            } else {
                id = result.id;
            }

            url = {
                additionalInfo: "",
                id,
                type: Const.SITE_TREE,
                description,
                url: `/api/${Const.SITE_TREE}/${id}`,
                openToNewTab: true,
            };

            description = "";
            break;
        case "customerDisplayDescription":
        case "customerDisplayName":
            if (searchIndex !== Const.SEARCH_RESULT_CUSTOMERS) {
                id = result.customerDisplayName;
            } else {
                id = result.internalId;
            }
            url = {
                additionalInfo: "",
                id,
                type: Const.CUSTOMERS_TREE,
                description,
                url: `/api/${Const.CUSTOMERS_TREE}/${result}`,
                openToNewTab: true,
            };
            description = "";
            break;
        case "customerId":
        case "customerName": {
            url = {
                additionalInfo: mapColumnNamesToColumNameAccessor(displayName),
                type: Const.CUSTOMERS_TREE,
                url: `/api/${Const.CUSTOMERS_TREE}/${result}`,
                id: result.customerId,
                description: description,
                openToNewTab: true,
            };
            description = "";
            break;
        }
        case "productDisplayDescription":
        case "productDisplayName":
            url = {
                additionalInfo: "",
                id: result.id,
                type: Const.PRODUCTS_TREE,
                description,
                url: `/api/${Const.PRODUCTS_TREE}/${result}`,
                openToNewTab: true,
            };
            description = "";
            break;
        case "documentId":
            let documentsEntityId: string;

            try {
                // Parse documentid from search id string. During download document id is used as x-ot-documentid header value.
                // Format: <base64 encoded document id>_<location alternate id>
                documentsEntityId = atob(result.id.split("_")[0]);
            } catch (err) {
                console.error("Failed to decode document id", result.id, err);
                documentsEntityId = "";
            }

            url = {
                additionalInfo: documentsEntityId,
                id: result.id,
                type: Const.LINK_TO_SAS,
                description,
                url: `/api/${Const.SITE_TREE}/${result.locationId}/documents/${result.documentFileName}`,
            };
            description = "";
            toolTip = result.documentFileName;
            break;
        default: {
            url = { id: "", type: "", description: "", url: "" };
            additionalInfo = mapColumnNamesToColumNameAccessor(displayName);
            break;
        }
    }

    return { description, url, toolTip, additionalInfo };
};

export const getOptionFields: (
    optionKeys: string[],
    options: Models.SearchOptions
) => Models.SearchSettingField[] = (optionKeys, options) => {
    const optionFields: Models.SearchSettingField[] = _.map(optionKeys, (key) =>
        _.merge({}, options[key], { key: key })
    );
    return optionFields;
};

export const getFilterGroups: (
    optionFields: Models.SearchSettingField[],
    searchIndex: string
) => any = (optionFields, searchIndex) => {
    let groups: any[] = _.chain(optionFields)
        .groupBy("group")
        .toPairs()
        .map((item) => _.zipObject(["groupType", "settingFields"], item))
        .value();

    groups = _.map(groups, (g: Models.SearchSettingGroup) => ({
        groupType: Number(g.groupType),
        settingFields: belongsToFilterGroup(g, searchIndex),
    }));

    return groups;
};

export const getNonEmptyFilters: (
    filters: Models.SearchFilters,
    optionKeys: string[]
) => string[] = (filters, optionKeys) => {
    const nonEmptyFilters: string[] = _.filter(optionKeys, (k: string) => {
        if (_.has(filters, k) && filters[k]?.length === 0) {
            return false;
        }

        const nonEmptyValueFilters: boolean =
            _.filter(filters[k], (f: Models.SearchFilterValue) => {
                if (
                    k === "buildWarrantyStartDate" ||
                    k === "buildCreatedDate" ||
                    k === "documentEffectiveDate" ||
                    k === "buildEndOfSupportDate" ||
                    k === "buildEndOfFullSupportDate"
                ) {
                    return f.compareFrom || f.compareTo;
                }
                return f.value && f.value.length > 0;
            }).length > 0;
        const nonEmptyComparisonFilters: boolean =
            _.filter(
                filters[k],
                (f: Models.SearchFilterValue) =>
                    f.type &&
                    f.type ===
                        Models.SearchSettingValueType.DateTimeComparison &&
                    ((f.compareTo && f.compareTo !== "") ||
                        (f.compareFrom && f.compareFrom !== ""))
            ).length > 0;

        if (nonEmptyValueFilters || nonEmptyComparisonFilters) {
            return true;
        } else {
            return false;
        }
    });
    return nonEmptyFilters;
};

export const getComparisonOperatorLabel: (key: string) => string = (key) => {
    const label: Models.ComparisonOperatorField = _.find(
        comparisonOperators,
        (o) => o.key === key
    );
    return label ? label.value : key;
};

const CustomDropdownIndicator = (props) => {
    const isMenuOpen = props.selectProps.menuIsOpen;

    return (
        <components.DropdownIndicator {...props}>
            {isMenuOpen ? (
                <OutotecIcons type={Const.SORT_UP} />
            ) : (
                <OutotecIcons type={Const.SORT_DOWN} />
            )}
        </components.DropdownIndicator>
    );
};

export const getFilterValues: (
    filters: Models.SearchFilters,
    options: Models.SearchOptions,
    group: Models.SearchSettingGroup,
    isPreview: boolean,
    removeFilter: any,
    editFilter?: any,
    onChangeOperator?: any
) => JSX.Element = (
    filters,
    options,
    group,
    isPreview,
    removeFilter,
    editFilter,
    onChangeOperator
) => {
    const optionKeys: string[] = _.keys(options);
    const optionFields: Models.SearchSettingField[] = getOptionFields(
        optionKeys,
        options
    );
    const nonEmptyFilters: string[] = getNonEmptyFilters(filters, optionKeys);
    const element: JSX.Element = (
        <div>
            {_.map(nonEmptyFilters, (key: string, x: number) => {
                const option: Models.SearchSettingField = options[key];
                const filterValues: Models.SearchFilterValue[] = filters[key];

                if (group && option.group !== group.groupType) {
                    return;
                }
                const isClassAttribute: boolean =
                    key === "engineeringClassAttributeNames";
                const showOperator: boolean =
                    isClassAttribute ||
                    option.elementType ===
                        Models.SearchSettingElementType.TextWithOperators ||
                    option.elementType ===
                        Models.SearchSettingElementType.SelectWithOperators;

                return (
                    <div
                        key={x.toString()}
                        className="AdvancedSearch__filterFieldContainer"
                    >
                        <span className="AdvancedSearch__filterFieldLabel">
                            {options[key].descriptionSingular}
                        </span>

                        {_.map(
                            filterValues,
                            (filterValue: Models.SearchFilterValue, i) => (
                                <span key={i.toString()}>
                                    <span className="AdvancedSearch__filterField">
                                        {options[key].elementType !==
                                            Models.SearchSettingElementType
                                                .SelectFromOptions &&
                                            option.valueType !==
                                                Models.SearchSettingValueType
                                                    .DateTimeComparison && (
                                                <span>
                                                    <span className="AdvancedSearch__filterFieldComparisonOperator">
                                                        {getComparisonOperatorLabel(
                                                            filterValue.comparisonOperator
                                                        )}
                                                    </span>
                                                    <span className="AdvancedSearch__filterFieldValue">
                                                        {filterValue.value}
                                                    </span>
                                                </span>
                                            )}

                                        {options[key].elementType !==
                                            Models.SearchSettingElementType
                                                .SelectFromOptions &&
                                            option.valueType ===
                                                Models.SearchSettingValueType
                                                    .DateTimeComparison && (
                                                <span>
                                                    {filterValue.compareTo &&
                                                        filterValue.compareTo !==
                                                            "" && (
                                                            <span>
                                                                <span className="AdvancedSearch__filterFieldComparisonOperator">
                                                                    &gt;=
                                                                </span>
                                                                <span className="AdvancedSearch__filterFieldValue">
                                                                    {
                                                                        filterValue.compareTo
                                                                    }
                                                                </span>
                                                            </span>
                                                        )}
                                                    {filterValue.compareFrom &&
                                                        filterValue.compareFrom !==
                                                            "" && (
                                                            <span>
                                                                <span className="AdvancedSearch__filterFieldComparisonOperator">
                                                                    &lt;=
                                                                </span>
                                                                <span className="AdvancedSearch__filterFieldValue">
                                                                    {
                                                                        filterValue.compareFrom
                                                                    }
                                                                </span>
                                                            </span>
                                                        )}
                                                </span>
                                            )}

                                        {options[key].elementType ===
                                            Models.SearchSettingElementType
                                                .SelectFromOptions && (
                                            <span>
                                                <span className="AdvancedSearch__filterFieldValue">
                                                    {filterValue.label}
                                                </span>
                                                <span className="AdvancedSearch__filterFieldComparisonOperator">
                                                    {filterValue.comparisonOperator
                                                        ? getComparisonOperatorLabel(
                                                              filterValue.comparisonOperator
                                                          )
                                                        : getComparisonOperatorLabel(
                                                              "eq"
                                                          )}
                                                </span>

                                                {filterValue.compareFrom && (
                                                    <span className="AdvancedSearch__filterCompareFrom">
                                                        {
                                                            filterValue.compareFrom
                                                        }
                                                    </span>
                                                )}
                                                {filterValue.compareFrom &&
                                                    filterValue.compareTo && (
                                                        <span className="AdvancedSearch__filterCompareAnd">
                                                            {" "}
                                                            and{" "}
                                                        </span>
                                                    )}
                                                <span className="AdvancedSearch__filterCompareTo">
                                                    {filterValue.compareTo}
                                                </span>
                                            </span>
                                        )}

                                        <span
                                            className="AdvancedSearch__filterAction AdvancedSearch__filterAction--remove"
                                            onClick={() =>
                                                removeFilter(key, filterValue)
                                            }
                                        >
                                            <OutotecIcons type={Const.REMOVE} />
                                        </span>
                                        {isClassAttribute && !isPreview && (
                                            <span
                                                className="AdvancedSearch__filterAction AdvancedSearch__filterAction--edit"
                                                onClick={() =>
                                                    editFilter(key, filterValue)
                                                }
                                            >
                                                <OutotecIcons
                                                    type={Const.EDIT}
                                                />
                                            </span>
                                        )}
                                    </span>
                                    {i < filterValues.length - 1 && (
                                        <span className="AdvancedSearch__filterFieldSeparator">
                                            {(!showOperator || isPreview) && (
                                                <span>
                                                    {filterValue.operator
                                                        ? filterValue.operator
                                                        : "or"}
                                                </span>
                                            )}
                                            {showOperator && !isPreview && (
                                                <span className="AdvancedSearch__filterFieldOperator">
                                                    <Select
                                                        className="Select--Search Select--SearchFilterOperator"
                                                        isSearchable={false}
                                                        isClearable={false}
                                                        value={
                                                            filterValue.operator
                                                                ? {
                                                                      label: filterValue.operator.toUpperCase(),
                                                                      value: filterValue.operator,
                                                                  }
                                                                : {
                                                                      label: "OR",
                                                                      value: "or",
                                                                  }
                                                        }
                                                        options={[
                                                            {
                                                                label: "OR",
                                                                value: "or",
                                                            },
                                                            {
                                                                label: "AND",
                                                                value: "and",
                                                            },
                                                        ]}
                                                        onChange={(
                                                            f: Models.ReactSelectOption
                                                        ) =>
                                                            onChangeOperator(
                                                                f,
                                                                key,
                                                                filterValue
                                                            )
                                                        }
                                                        components={{
                                                            DropdownIndicator:
                                                                CustomDropdownIndicator,
                                                        }}
                                                        styles={{
                                                            dropdownIndicator: (
                                                                base
                                                            ) => ({
                                                                ...base,
                                                                padding: 8,
                                                                svg: {
                                                                    fill: "#009CD9",
                                                                    width: 9,
                                                                    height: 9,
                                                                },
                                                                cursor: "pointer",
                                                            }),
                                                            control: (
                                                                styles,
                                                                { isFocused }
                                                            ) => ({
                                                                ...styles,
                                                                borderColor:
                                                                    isFocused
                                                                        ? "lightgrey"
                                                                        : "lightgrey", // Remove blue border on focus
                                                                boxShadow:
                                                                    "none", // Remove any box shadow (blue highlight)
                                                                ":hover": {
                                                                    borderColor:
                                                                        "lightgrey",
                                                                },
                                                                minHeight: 35,
                                                            }),
                                                            placeholder: (
                                                                styles
                                                            ) => ({
                                                                ...styles,
                                                                fontStyle:
                                                                    "italic",
                                                                color: "#ccc",
                                                            }),
                                                        }}
                                                    />
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </span>
                            )
                        )}
                    </div>
                );
            })}
        </div>
    );

    return element;
};

export const searchIndexToGroupType: (
    searchIndex: string
) => Models.SearchSettingGroupType = (searchIndex) => {
    switch (searchIndex) {
        case Const.SEARCH_RESULT_BUILDS:
            return Models.SearchSettingGroupType.Build;
        case Const.SEARCH_RESULT_LOCATIONS:
            return Models.SearchSettingGroupType.Location;
        case Const.SEARCH_RESULT_CUSTOMERS:
            return Models.SearchSettingGroupType.Customer;
        case Const.SEARCH_RESULT_PRODUCTS:
            return Models.SearchSettingGroupType.Product;
        case Const.SEARCH_RESULT_DOCUMENTS:
            return Models.SearchSettingGroupType.Document;
    }
};

export const groupTypeToString: (
    groupType: Models.SearchSettingGroupType
) => string = (groupType) => {
    switch (groupType) {
        case Models.SearchSettingGroupType.Build:
            return Const.SEARCH_FILTER_GROUP_BUILD;
        case Models.SearchSettingGroupType.Location:
            return Const.SEARCH_FILTER_GROUP_LOCATION;
        case Models.SearchSettingGroupType.Customer:
            return Const.SEARCH_FILTER_GROUP_CUSTOMER;
        case Models.SearchSettingGroupType.Product:
            return Const.SEARCH_FILTER_GROUP_PRODUCT;
        case Models.SearchSettingGroupType.Document:
            return Const.SEARCH_FILTER_GROUP_DOCUMENT;
    }
};

export const belongsToFilterGroup: (
    group: Models.SearchSettingGroup,
    searchIndex: string
) => Models.SearchSettingField[] = (group, searchIndex) => {
    const resultViewGroupType: Models.SearchSettingGroupType =
        searchIndexToGroupType(searchIndex);
    return _.filter(group.settingFields, (field) => {
        const a: number = _.findIndex(
            field.belongsToResultViews,
            (type) => type === resultViewGroupType
        );
        return a !== -1;
    });
};

export const optionsToColumns: (options: any, searchIndex: string) => any = (
    options,
    searchIndex
) => {
    const sortedOptions: Models.SearchSettingField[] = _.sortBy(
        _.values(options),
        (o: Models.SearchSettingField) => {
            return o.order;
        }
    );

    let columnValues: Models.SearchResultColumn[] = _.map(
        sortedOptions,
        (o: Models.SearchSettingField) => {
            return {
                searchField: o.searchIndexField,
                displayName: o.columnHeaderLabel,
                active: false,
                columnFieldInResultViews: o.columnFieldInResultViews,
                isColumn: o.isColumn,
                filterable:
                    o.valueType === Models.SearchSettingValueType.String,
                classAttribute: false,
                filterGroupOrder: o.filterGroupOrder,
                searchResultOrders: o.searchResultOrders,
                group: o.group,
            };
        }
    );

    return _.filter(columnValues, (cv) => cv.isColumn);
};

export const mapColumnNamesToColumNameAccessor: (name: string) => any = (
    name
) => {
    if (name && name?.length > 0) {
        if (name.includes(" ")) {
            return name.replace(/ /g, "_").toLowerCase();
        }
        return name.toLowerCase();
    }
};
